import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import InfiniteList from '../../../InfiniteList';

const {
  defaultProps: InfiniteListDefaultProps,
  propTypes: InfiniteListPropTypes,
} = InfiniteList;

const ITEM_SIZE = 105;

const STYLES = {
  MARGIN_RIGHT: 10,
  MARGIN_TOP: 12,
};

const Context = React.createContext();

class InfiniteTabs extends React.PureComponent {
  static getDerivedStateFromProps({ value: propsValue }, { prevPropsValue }) {
    if (propsValue === prevPropsValue) {
      return null;
    }

    return {
      prevPropsValue: propsValue,
      value: propsValue,
    };
  }

  static getInnerElement({ children, ...props }) {
    return (
      <Context.Consumer>
        {({
          child,
          containerEl,
          index,
          isStickyOnBottom,
          isStickyOnTop,
          itemData,
          itemSize,
          value,
          styles,
        }) => {
          const isSticky = isStickyOnBottom || isStickyOnTop;
          const containerSize = _.get(containerEl, 'clientHeight') || 0;

          let stickyTab = null;
          if (isSticky) {
            const component = child({
              data: itemData,
              index,
              styles,
              style: {
                height: itemSize,
                top: isStickyOnTop ? 0 : containerSize - itemSize,
              },
            });

            if (component && value === component.props.value) {
              stickyTab = (
                <StyledTab
                  component={component}
                  selected
                  data-sticky={isSticky}
                  sticky={isSticky}
                  stickyBottom={isStickyOnBottom}
                  stickyTop={isStickyOnTop}
                  styles={styles}
                />
              );
            }
          }

          return (
            <StyledTabs {...props}>
              {stickyTab}
              {children}
            </StyledTabs>
          );
        }}
      </Context.Consumer>
    );
  }

  constructor(props) {
    super(props);

    const { value } = props;
    this.state = {
      isStickyOnBottom: false,
      isStickyOnTop: false,
      prevPropValue: value,
      value,
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleItemsRendered = this.handleItemsRendered.bind(this);
    this.handleRefList = this.handleRefList.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    this.setAndCheckSelectedTabElement();
  }

  componentDidUpdate() {
    this.setAndCheckSelectedTabElement();
  }

  /**
   * Метод ищет элемент выделенного таба и, если находит, то проверяет, должен ли он быть прикреплён
   * к верху или низу элемента контейнера компонента.
   */
  setAndCheckSelectedTabElement() {
    if (!this.el) {
      this.selectedTabEl = null;
      return;
    }

    const { value } = this.state;
    this.selectedTabEl = this.el.querySelector(
      `li[value="${value}"]:not([data-sticky="true"])`
    );
    if (!this.selectedTabEl) {
      this.selectedTabEl = null;
      return;
    }

    this.selectedIndex = this.selectedTabEl.dataset.index;
    this.checkSticky();
  }

  /**
   * Метод определеляет, должен ли быть прикреплён элемент выделенного таба к верху или низу
   * элемента контейнера компонента.
   */
  checkSticky() {
    if (!this.el || !this.selectedTabEl) {
      return;
    }

    const item = this.selectedTabEl;
    const itemRect = item.getBoundingClientRect();
    const list = this.el;
    const listRect = list.getBoundingClientRect();

    const { height, width } = itemRect;
    if (height === 0 && width === 0) {
      return;
    }

    const listStyle = window.getComputedStyle(list);
    const listTopOffset =
      Number(listStyle.borderTopWidth.replace(/\D+/g, '')) +
      Number(listStyle.paddingTop.replace(/\D+/g, ''));

    const isStickyOnTop =
      list.scrollTop && itemRect.y - listTopOffset <= listRect.y;

    let isStickyOnBottom = false;
    if (!isStickyOnTop) {
      const isScrollBottom =
        list.scrollHeight - list.scrollTop !== list.clientHeight;
      const listBottomOffset =
        Number(listStyle.borderBottomWidth.replace(/\D+/g, '')) +
        Number(listStyle.paddingBottom.replace(/\D+/g, ''));
      const isSticky =
        itemRect.y >=
        listRect.y + list.offsetHeight - listBottomOffset - item.offsetHeight;
      isStickyOnBottom = isScrollBottom && isSticky;
    }

    this.setState({
      isStickyOnBottom: !!isStickyOnBottom,
      isStickyOnTop: !!isStickyOnTop,
    });
  }

  handleClick(event) {
    const { value } = this.state;
    const tabElement = event.target.closest('li');
    const nextValue = tabElement.getAttribute('value');
    if (!nextValue || nextValue === value) {
      return;
    }

    const { onSelect } = this.props;
    this.setState({ value: nextValue }, () => {
      onSelect(nextValue);
      this.setAndCheckSelectedTabElement();
    });
  }

  /**
   * Метод следит за отображением выделенного таба в DOM-е, потому что таб может пропасть, а после
   * его появления нужно заново найти его элемент. Если выделенный таб не успел добавиться в DOM
   * (такое возможно, например, если очень быстро промотать список), то тогда определение позиции
   * залипания происходит при сравнении с индексами отображённых табов.
   */
  handleItemsRendered({ overscanStartIndex, overscanStopIndex }) {
    if (_.inRange(this.selectedIndex, overscanStartIndex, overscanStopIndex)) {
      this.setAndCheckSelectedTabElement();
    } else {
      this.setState({
        isStickyOnBottom: this.selectedIndex > overscanStopIndex,
        isStickyOnTop: this.selectedIndex < overscanStartIndex,
      });
    }
  }

  handleRefList(component) {
    this.el = ReactDOM.findDOMNode(component);
  }

  handleScroll() {
    this.checkSticky();
  }

  render() {
    const {
      children: child,
      itemData,
      itemSize,
      hasNextPage,
      styles,
      ...props
    } = this.props;
    const { isStickyOnBottom, isStickyOnTop, value } = this.state;

    return (
      <StyledInfiniteTabs>
        <Context.Provider
          value={{
            child,
            containerEl: this.el,
            index: this.selectedIndex,
            isStickyOnBottom,
            isStickyOnTop,
            itemData,
            itemSize,
            value,
          }}
        >
          <StyledInfiniteList
            {...props}
            hasNextPage={hasNextPage}
            innerElementType={InfiniteTabs.getInnerElement}
            itemData={itemData}
            itemSize={itemSize + styles.MARGIN_TOP}
            onItemsRendered={this.handleItemsRendered}
            onScroll={this.handleScroll}
            ref={this.handleRefList}
          >
            {({ data, index, style }) => {
              const component = child({
                data,
                index,
                style: {
                  ...style,
                  height: style.height - styles.MARGIN_TOP,
                  right: styles.MARGIN_RIGHT,
                },
                styles,
              });

              if (!component) {
                return null;
              }

              return (
                <StyledTab
                  data-index={index}
                  component={component}
                  onClick={this.handleClick}
                  selected={value === component.props.value}
                  styles={styles}
                />
              );
            }}
          </StyledInfiniteList>
        </Context.Provider>
      </StyledInfiniteTabs>
    );
  }
}

InfiniteTabs.propTypes = {
  ...InfiniteListPropTypes,
  itemSize: PropTypes.number,
  onSelect: PropTypes.func,
  value: PropTypes.string,
};

InfiniteTabs.defaultProps = {
  ...InfiniteListDefaultProps,
  itemSize: ITEM_SIZE,
  onSelect: Function.prototype,
  value: null,
  styles: STYLES,
};

const StyledInfiniteTabs = styled.div`
  height: 100%;
  width: 100%;
`;

const StyledInfiniteList = styled(InfiniteList)`
  ::-webkit-scrollbar {
    width: 0;
  }
`;

const StyledTabs = styled.ul`
  border-radius: 4px;
  position: relative;
`;

const StyledTab = styled(({ component, ...props }) =>
  React.cloneElement(component, props)
)`
  box-sizing: border-box;
  width: auto !important;

  > button {
    outline: none; // Sorry, not this time! :(
    text-align: left;
  }

  ${({ selected, styles }) =>
    selected &&
    `
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    padding-right: ${styles?.MARGIN_RIGHT ?? STYLES.MARGIN_RIGHT}px;
    right: 0 !important;
  `}

  ${({ sticky }) =>
    sticky &&
    `
    position: sticky;
    z-index: 2;
  `}
  
  ${({ stickyTop }) =>
    stickyTop &&
    `
    box-shadow: 0 -5px 0 0 #e6e6e6, 
                0 10px 15px 0 rgba(196, 194, 182, 0.9),
                0 1px 2px 0 rgba(210, 208, 197, 0.9);
  `}
  
  ${({ stickyBottom }) =>
    stickyBottom &&
    `
    box-shadow: 0 -10px 15px 0 rgba(196, 194, 182, 0.9),
                0 -1px 2px 0 rgba(210, 208, 197, 0.9),
                0 5px 0 0 #e6e6e6;
  `}
`;

export default InfiniteTabs;
